import { useEffect, useRef } from "react";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { AudioTrack } from "providers/AudioProvider/types";
import { originalPositionMs } from "utils/originalMedia";
import { batchHolds, clearHolds, holdPlayback, setVoicingGate } from "utils/playbackHold";
import { clearAlignedStarts, consumeUserSeek, requestAlignedStart } from "utils/playbackIntent";
import {
    clipIsHopeless,
    clipIsReady,
    clipMedia,
    forgetClips,
    publishClipState,
    requestClips,
    setAwaitedClip,
    setKnownClips,
} from "utils/clipRegistry";

/**
 * Decide which voicing is sounding, and stop the world until it can — the only thing in the editor
 * that decides either.
 *
 * Four mechanisms used to share the first job: each fragment selected itself from
 * `playOriginalStart`; a finished fragment chained to the next through `setTimeout(gap)`; the
 * original's clock promoted `playingFragmentNext` when it reached its window; and clicks set the
 * fragment directly. They disagreed constantly, and every symptom reported came out of that
 * disagreement — a voicing playing twice because two of them selected it, a short one never heard
 * because a third replaced it within 100ms, playback stopping altogether when a chained timeout
 * fired for a moment the original had already passed.
 *
 * None of it was needed. The fragments are positioned on the original's own timeline: `start` and
 * `length` are milliseconds of the Russian audio. So the question "what should be sounding" has
 * one answer, and it is a lookup — the cue whose window contains the playhead.
 *
 * The SECOND job is newer and is the reason this file changed again. A cue that cannot make sound
 * yet must stop everything — the lecturer AND the picture — until it can. That used to be attempted
 * in two other places and reached neither of the cases that mattered: the clip's own player cannot
 * hold anything before its element exists, and the wait below the handover was unreachable for a
 * clicked cue, because `if (id === current) return` sat one line above it. So the gate now runs at
 * the top, before every early return, and it asks the clip registry rather than the DOM.
 */

/** How often the playhead is checked. Fine enough that a cue starts within a frame or two. */
const TICK_MS = 25;

/**
 * How much further than time allows the playhead may move before it counts as a seek.
 *
 * NOT a raw tick-to-tick distance. That was the earlier rule, and it was wrong: it assumed a tick
 * always arrives 25ms after the last one, so any hitch on the main thread — a folder of waveforms
 * being decoded, say — delayed a tick past the threshold and the playhead appeared to "jump" while
 * it had merely been playing normally the whole time. The schedule then treated it as a gesture,
 * which has a specific and very audible consequence: a cue entered part-way through, at the
 * "matching words", instead of from its first word. Measured on the deployed editor: 7 of 15 cues
 * started between 0.4s and 1.1s in.
 *
 * Comparing against the time that actually elapsed removes the assumption. A late tick moves the
 * playhead exactly as far as the delay allows, and is not a gesture; a seek moves it further.
 */
const JUMP_MS = 400;

/** Moving backwards by more than this is a seek — playback never rewinds on its own. */
const REWIND_MS = 250;

/**
 * Take a cue's turn slightly before the lecturer reaches it.
 *
 * The turn is where the whole start sequence begins — claim the hold, rewind, play, wait for
 * sound — and every step of it takes time the original would otherwise spend talking. Beginning
 * fractionally early, and stopping the original while it happens, means the translation is heard
 * from the moment the lecturer says the line rather than shortly after it.
 */
const LEAD_MS = 150;

/**
 * How long an explicit choice outranks the lookup.
 *
 * Clicking a cue sets it directly and seeks the original to it, but the seek does not land in the
 * same tick. Without this the lookup would see the old position, decide a different cue covers it
 * and take the click back before it was heard.
 */
const EXPLICIT_MS = 1500;

/**
 * How long one of this hook's own writes may take to come back through state.
 *
 * Measured under a folder's render load: a few ticks, well under 200ms. Past this, a mismatch is
 * not our write in flight — it is an external choice and must be adopted, not waited on.
 */
const WRITE_DEADLINE_MS = 600;

/**
 * A reposition landing deeper than this into a cue starts the clip at the matching offset.
 *
 * Entering a cue at its natural boundary (or a click within the first words) starts from the top,
 * with the usual hold-and-lead. Deeper than this, restarting the sentence against a lecturer who
 * is half-way through it is the mismatch that was audible as hiccups after every wave click — the
 * words belong at the words.
 */
const ALIGN_MIN_MS = 300;

/**
 * How many cues ahead of the playhead to have prepared.
 *
 * This replaced `state.playing` in the row's own loading condition, which enabled EVERY row of the
 * folder the instant playback started — 72 of them on the folder this was measured on, all
 * preparing at once, so the one actually needed next got no more of the machine than the 71 that
 * were not. A window that walks with the playhead gets the same clips ready in the same order they
 * will be heard.
 */
const PREFETCH_AHEAD = 8;

/**
 * How long a cue may fail to become playable before it is treated as broken.
 *
 * NOT a budget for waiting. The wait is deliberately unbounded — how long a clip takes to become
 * playable is a property of the machine, and cutting the wait short is exactly the reported bug.
 * This is only a deadlock backstop, so a cue that will never be ready costs a pause rather than
 * the rest of the lecture. The translator can also give up sooner, from the badge.
 */
const STALL_GUARD_MS = 30000;

/** Can this cue be passed over — either because it can play, or because it never will? */
const canSound = (id: string): boolean => clipIsReady(id) || clipIsHopeless(id);

interface Options {
    /** Voicing tracks of the language on screen, in folder order. */
    tracks: AudioTrack[];
}

export const useVoicingSchedule = ({ tracks }: Options) => {
    const state = useAudioStateContext();
    const handles = useAudioHandlesContext();

    /**
     * THE SCHEDULER MUST SURVIVE RENDERS. Measured on the deployed editor: the provider
     * re-renders five times a second while playing (the playbackTime publisher), each render
     * costs on the order of 200ms across a folder of rows, and the `handles` context value has a
     * new identity every time. With `handles` in the effect's deps, every render tore the tick
     * interval down and made a new one — which, on a machine where renders arrive faster than
     * the thread goes idle, means the tick NEVER fires: intervals lived ~200ms each and fired
     * exactly zero times, for the whole session. No tick — no gate, no handovers: the lecturer
     * ran through unready cues, and the first starved tick that did land mid-cue read the
     * accumulated movement as a seek. That is the reported "russian goes without waiting, and
     * when it remembers to wait the voicing jumps to this position", verbatim.
     */
    const handlesRef = useRef(handles);
    handlesRef.current = handles;

    const tracksRef = useRef<AudioTrack[]>(tracks);
    tracksRef.current = tracks;

    const currentRef = useRef<string | null>(state.playingFragment);
    currentRef.current = state.playingFragment;

    const playingRef = useRef<boolean>(state.playing);
    playingRef.current = state.playing;

    /** Needed to know how far the playhead SHOULD have moved since the last tick. */
    const rateRef = useRef<number>(Number(state.playbackRate) || 1);
    rateRef.current = Number(state.playbackRate) || 1;

    /**
     * Cues already handed over during this pass.
     *
     * A cue's window is longer than the voicing inside it — that is the whole point of the folder,
     * the translation is shorter than the original. So the playhead is still inside a cue when its
     * voicing finishes, and without this the lookup would hand the same cue over again and it
     * would play a second time. Cleared whenever the playhead jumps, so a deliberate click on a
     * cue that has already played always plays it again.
     */
    const playedRef = useRef<Set<string>>(new Set());
    const lastPosRef = useRef<number | null>(null);
    /** Wall clock at the previous tick, so a late tick can be told from a seek. */
    const lastTickAtRef = useRef<number | null>(null);
    /** The playhead just moved by a gesture; the next selection may need an aligned start. */
    const jumpedRef = useRef(false);
    /**
     * What this hook has asked for, and what it asked for before that.
     *
     * State does not come back on the tick that wrote it — with a folder of cues re-rendering, it
     * can take several. A first version allowed a fixed 250ms for that and then assumed anything
     * else was somebody else's choice, which under load meant the hook regularly mistook its own
     * instruction for a click and stood down for a second and a half. Cues whose turn fell inside
     * that window were skipped, or picked up late, mid-line.
     *
     * Two values need no timeout at all: if what state reports is what was asked for BEFORE the
     * outstanding request, the write is simply still in flight. Anything else is genuinely someone
     * else — a click, a shortcut, the saved-track review.
     */
    const wantRef = useRef<string | null>(null);
    const wantedBeforeRef = useRef<string | null>(null);
    /** When this hook last wrote; bounds how long "write in flight" may explain a mismatch. */
    const wroteAtRef = useRef<number>(0);
    const explicitUntilRef = useRef<number>(0);
    /** Which cue everything is currently stopped for, and since when. */
    const waitingForRef = useRef<{ id: string; since: number } | null>(null);

    // The footer counts readiness over the whole folder, so the registry has to know what the
    // whole folder is — not merely the handful of cues that have been asked for so far.
    useEffect(() => {
        setKnownClips(tracks.map((track) => String(track.id)));
    }, [tracks]);

    useEffect(() => {
        // Every hold and gate change this tick makes is announced once, at the end — see
        // batchHolds. Opening the gate and claiming the next clip's hold are two steps of ONE
        // decision, and letting subscribers see the moment in between told the original to play
        // and then immediately to pause.
        const tick = () => batchHolds(() => {
            const pos = originalPositionMs();
            if (pos === null) {
                setVoicingGate(null);
                setAwaitedClip(null);
                return;
            }

            const previous = lastPosRef.current;
            lastPosRef.current = pos;

            const now = Date.now();
            const sinceTick = lastTickAtRef.current === null ? null : now - lastTickAtRef.current;
            lastTickAtRef.current = now;

            // A jump starts a new pass: everything is allowed to play again. An announced
            // user seek additionally cancels any standing courtesy toward an earlier click —
            // the newest gesture always wins.
            const userSeek = consumeUserSeek();
            if (userSeek) {
                playedRef.current.clear();
                jumpedRef.current = true;
                explicitUntilRef.current = 0;
            }

            // DID SOMEBODY MOVE THE PLAYHEAD, OR DID TIME SIMPLY PASS?
            //
            // The playhead advancing is not news; advancing further than the clock allows is. A
            // held cue does not move at all (moved ≈ 0 while the clock runs on) and that is not a
            // gesture either — only movement the elapsed time cannot account for is.
            const moved = previous === null ? 0 : pos - previous;
            const allowed = sinceTick === null ? 0 : sinceTick * (rateRef.current || 1);
            const seeked =
                previous === null || moved < -REWIND_MS || moved > allowed + JUMP_MS;
            if (seeked) {
                playedRef.current.clear();
                jumpedRef.current = true;
            }

            const list = tracksRef.current;
            if (!list?.length) return;

            const current = currentRef.current;
            const covering = findCovering(list, pos);
            const coveringId = covering ? String(covering.id) : null;

            // GET THE NEXT CLIPS READY, in the order they will be heard. Runs even while stopped,
            // so pressing play does not begin with a wait.
            requestUpcoming(list, pos, current);

            if (!playingRef.current) {
                setVoicingGate(null);
                setAwaitedClip(null);
                return;
            }

            // ── THE GATE ─────────────────────────────────────────────────────────────────────
            //
            // NOTHING MOVES UNTIL THE CUE THAT SHOULD BE SOUNDING CAN SOUND. This runs before
            // every other decision in the tick, and that position is the fix: the previous
            // version waited further down, below `if (id === current) return`, so a cue chosen by
            // a CLICK — which sets `current` itself — reached the return and never the wait. The
            // lecturer and the video simply carried on while the clip was still being prepared,
            // which is what was reported.
            //
            // `current` is checked first and separately from the covering cue, because those are
            // two different failures: a cue somebody just chose that is not ready, and the next
            // cue arriving while the previous one is still playing.
            const awaited =
                current && !canSound(current)
                    ? current
                    : coveringId && !playedRef.current.has(coveringId) && !canSound(coveringId)
                      ? coveringId
                      : null;

            // ── LET THE VOICING FINISH ───────────────────────────────────────────────────────
            //
            // A cue's window is the length of the RUSSIAN line, and a translation is frequently
            // longer than the line it translates. When the playhead reached the next cue the
            // schedule handed over, which pauses the clip that is still speaking — so the last
            // words were cut off. Measured on the deployed editor: 8 of 9 voicings were cut, two
            // of them by more than a fifth (1.38s played of 1.86s; 2.35s of 3.02s). Cue after cue,
            // that is what "hiccups, does not play fully" sounds like.
            //
            // So the lecturer waits at the boundary instead. The clip is not touched — it is still
            // the current one, so nothing pauses it — and holding the playhead keeps the next cue
            // from taking its turn. When the voicing has said its piece, everything moves on
            // together. A clip that is not sounding (finished, failed, never started) holds nobody.
            if (current && coveringId !== current) {
                const speaking = clipMedia(current);
                if (speaking && !speaking.paused && !speaking.ended) {
                    setAwaitedClip(null);
                    setVoicingGate(current);
                    return;
                }
            }

            if (awaited) {
                const waiting = waitingForRef.current;
                if (!waiting || waiting.id !== awaited) {
                    waitingForRef.current = { id: awaited, since: Date.now() };
                }
                if (Date.now() - (waitingForRef.current?.since ?? Date.now()) < STALL_GUARD_MS) {
                    setAwaitedClip(awaited);
                    setVoicingGate(awaited);
                    return;
                }
                // Half a minute is not slowness, it is a clip that is never coming. Say so, and
                // let the lecture go on — the row turns red and the schedule passes it over.
                publishClipState(awaited, "error", "не удалось подготовить за 30 с");
            }
            waitingForRef.current = null;
            setAwaitedClip(null);
            setVoicingGate(null);

            // Not what this hook asked for.
            if (current !== wantRef.current) {
                // Still showing the previous request: this hook's latest one has not landed yet.
                // But only for as long as a write can plausibly take. "Looks like the previous
                // request" is also what a click's setPlayingFragment(null) looks like when the
                // previous request WAS null — the very first cue of a session — and treating that
                // as in-flight forever deadlocked the schedule: the original played on, the
                // interface followed the time, and no voicing was ever selected again.
                // A user seek settles the question outright: our writes do not move the
                // playhead, so the mismatch cannot be one of them in flight. Waiting out the
                // deadline here was audible — the voicing joined the clicked position late.
                if (
                    !userSeek &&
                    current === wantedBeforeRef.current &&
                    Date.now() - wroteAtRef.current < WRITE_DEADLINE_MS
                ) {
                    return;
                }
                wantedBeforeRef.current = current;
                wantRef.current = current;
                wroteAtRef.current = Date.now();
                // Somebody chose a cue. Respect it, count it as played so it is not handed over
                // twice, and keep out of the way until the playhead has caught up with it.
                if (current) {
                    playedRef.current.add(current);
                    explicitUntilRef.current = Date.now() + EXPLICIT_MS;
                    return;
                }
                // An external CLEAR while playback runs is a reposition (the wave click writes
                // null): adopt it and pick the covering cue in this same tick, not the next one.
            }
            if (Date.now() < explicitUntilRef.current) return;

            // Past the end of whatever is playing, or in a gap: nothing should be sounding.
            if (!covering) {
                if (current) {
                    wantedBeforeRef.current = wantRef.current;
                    wantRef.current = null;
                    wroteAtRef.current = Date.now();
                    handlesRef.current.setPlayingFragment(null);
                }
                return;
            }

            const id = String(covering.id);
            if (id === current) return;

            if (playedRef.current.has(id)) {
                // Its turn is over but the playhead has not left its window yet. Silence, not a
                // repeat — the lecturer is still talking, the translation has finished saying it.
                if (current) {
                    wantedBeforeRef.current = wantRef.current;
                    wantRef.current = null;
                    wroteAtRef.current = Date.now();
                    handlesRef.current.setPlayingFragment(null);
                }
                return;
            }

            // A cue that will never sound must not be waited for OR handed over: marking it
            // played is what lets the playhead move past it.
            if (clipIsHopeless(id)) {
                playedRef.current.add(id);
                if (current) {
                    wantedBeforeRef.current = wantRef.current;
                    wantRef.current = null;
                    wroteAtRef.current = Date.now();
                    handlesRef.current.setPlayingFragment(null);
                }
                return;
            }

            playedRef.current.add(id);
            wantedBeforeRef.current = wantRef.current;
            wantRef.current = id;
            wroteAtRef.current = Date.now();
            // A gesture that lands deep inside a cue enters the clip at the matching words. No
            // hold in that case: mid-sentence there is no clean entry to wait for, and pausing
            // the original right after the user clicked was itself one of the hiccups.
            let aligned = false;
            if (jumpedRef.current) {
                const offsetMs = pos - Number(covering.start);
                if (offsetMs > ALIGN_MIN_MS) {
                    requestAlignedStart(id, offsetMs);
                    aligned = true;
                }
            }
            jumpedRef.current = false;
            if (aligned) {
                handlesRef.current.setPlayingFragment(id);
                return;
            }
            // STOP THE LECTURER HERE, not once React has caught up.
            //
            // The clip claims the hold itself as well, but that only happens after this write has
            // rendered — and with a folder's worth of cues on screen that is not immediate. Those
            // milliseconds are the original talking over a translation that has not started.
            // Claiming it at the moment of the decision removes the render from the path; the clip
            // releases it when it makes sound, and the claim expires by itself if the clip never does.
            holdPlayback(id);
            handlesRef.current.setPlayingFragment(id);
        });

        const timer = window.setInterval(tick, TICK_MS);
        return () => window.clearInterval(timer);
        // Mounted once. The tick reads everything it needs through refs precisely so that no
        // render can restart it — see the note on handlesRef above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Stopping ends the pass, so pressing play again replays from wherever the playhead is.
    useEffect(() => {
        if (!state.playing) {
            playedRef.current.clear();
            lastPosRef.current = null;
            lastTickAtRef.current = null;
            waitingForRef.current = null;
            setAwaitedClip(null);
            // One owner of "drop every claim". This used to live in AudioTrackOriginal, which is
            // one of the things the gate holds — a held medium deciding when holds end.
            clearHolds();
            clearAlignedStarts();
        }
    }, [state.playing]);

    // A different folder or language is a different set of cues entirely.
    useEffect(() => {
        playedRef.current.clear();
        lastPosRef.current = null;
        lastTickAtRef.current = null;
        waitingForRef.current = null;
        setVoicingGate(null);
        clearAlignedStarts();
        forgetClips();
        // forgetClips() also drops the list of what the folder CONTAINS, and the effect that
        // publishes it runs before this one on the render where both change — so without this the
        // footer would read "0/0 ready" for the whole of the new folder.
        setKnownClips(tracksRef.current.map((track) => String(track.id)));
    }, [state.folder, state.lang]);
};

/**
 * The cue whose window contains the playhead — the LAST one, where several match.
 *
 * `adjustAllFragments` packs voicings 50ms apart, and a cue's turn begins LEAD_MS (150ms) before
 * its start, so at a boundary two cues genuinely match at once: the one just finishing and the one
 * about to begin. Taking the first match picks the cue that has already been heard, and the new
 * one is passed over — audible as a missing translation exactly at the moments where cues are
 * tightly packed.
 */
const findCovering = (tracks: AudioTrack[], pos: number): AudioTrack | null => {
    let covering: AudioTrack | null = null;
    for (const track of tracks) {
        const from = Number(track?.start);
        const len = Number(track?.length);
        if (!Number.isFinite(from) || !Number.isFinite(len)) continue;
        if (from - LEAD_MS > pos) break; // sorted by start: nothing after this can match either
        if (pos >= from - LEAD_MS && pos <= from + len) covering = track;
    }
    return covering;
};

/**
 * Ask for the clips that are about to be needed, nearest first.
 *
 * The cue currently selected leads the list even when it is behind the playhead: a shortcut can
 * choose a cue that is off screen and outside the window, and that cue is precisely the one
 * everything is now waiting for.
 */
const requestUpcoming = (tracks: AudioTrack[], pos: number, current: string | null): void => {
    const wanted: string[] = [];
    if (current) wanted.push(current);
    for (const track of tracks) {
        if (wanted.length >= PREFETCH_AHEAD + (current ? 1 : 0)) break;
        const from = Number(track?.start);
        const len = Number(track?.length);
        if (!Number.isFinite(from) || !Number.isFinite(len)) continue;
        if (from + len < pos - LEAD_MS) continue; // already gone by
        const id = String(track.id);
        if (!wanted.includes(id)) wanted.push(id);
    }
    requestClips(wanted);
};
